import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { ChatControlWrapper } from '@/app/chat/[avatarId]/frontend/shared/ChatControls';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';


interface ErrorProps {
  error: string;
  avatarName?: string;
}

export function Loading() {
  const t = useTranslations('chat');
  return (
    <div className="w-full flex items-center justify-center bg-[#121214]">
      <div className="flex flex-row items-center justify-center w-full max-w-[95vw] h-[100dvh] gap-0">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin" />
            <div className="text-white/80 text-lg font-medium">{t('loadingCharacter')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Error({ error, avatarName }: ErrorProps) {
  const t = useTranslations('chat');
  const router = useRouter();

  const handleGoHome = () => {
    router.push('/');
  };

  const handleGoBack = () => {
    router.back();
  };

  if (error === 'Character not found') {
    return (
      <div className="w-full flex items-center justify-center bg-[#121214]">
        <div className="flex flex-row items-center justify-center w-full max-w-[95vw] h-[100dvh] gap-8">
          <div className="w-full h-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-6 text-center max-w-md">
              <div className="text-6xl text-white/20 font-bold mb-4">404</div>
              <h1 className="text-white text-3xl font-semibold mb-2">{t('characterNotFound')}</h1>
              <p className="text-white/70 text-lg mb-6">
                {t('characterNotFoundDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={handleGoHome}
                  className="bg-[#4f46e5] hover:bg-[#5856d6] text-white rounded-lg px-6 py-3 text-sm font-medium transition-colors"
                >
                  {t('returnToHome')}
                </button>
                <button 
                  onClick={handleGoBack}
                  className="bg-[#2a2a2e] hover:bg-[#3a3a3e] text-white rounded-lg px-6 py-3 text-sm font-medium transition-colors"
                >
                  {t('goBack')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Generic error state
  return (
    <div className="w-full flex items-center justify-center bg-[#121214]">
      <div className="flex flex-row items-center justify-center w-full max-w-[95vw] h-[100dvh] gap-8">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 text-center max-w-md">
            <div className="text-6xl text-red-400/20 font-bold mb-4">!</div>
            <h1 className="text-white text-3xl font-semibold mb-2">{t('somethingWentWrong')}</h1>
            <p className="text-white/70 text-lg mb-6">
              {error || t('unexpectedError')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={handleGoHome}
                className="bg-[#4f46e5] hover:bg-[#5856d6] text-white rounded-lg px-6 py-3 text-sm font-medium transition-colors"
              >
                {t('returnToHome')}
              </button>
              <button 
                onClick={handleGoBack}
                className="bg-[#2a2a2e] hover:bg-[#3a3a3e] text-white rounded-lg px-6 py-3 text-sm font-medium transition-colors"
              >
                {t('goBack')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MobileChatLoading() {
  return (
    <div className="bg-[#121214] min-h-screen w-full flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#5856d6] animate-spin" />
    </div>
  );
}

export function VideoPreparingState({ avatarName }: { avatarName: string }) {
  return (
    <div className="bg-[#121214] min-h-screen w-full flex flex-row justify-center">
      <div className="w-full relative">
        <main className="flex flex-col w-full h-full items-center justify-center px-4 lg:px-0 pl-64">
          <div className="flex flex-row items-center justify-center w-full max-w-[95vw] h-[calc(100dvh-30px)] gap-8 py-[15px]">
            
            {/* Character Image Placeholder */}
            <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] rounded-[5px] bg-black shadow-lg flex-shrink-0" />
            
            {/* Loading Card */}
            <div className="w-full lg:w-auto lg:h-full aspect-[9/16] flex-shrink-0">
              <div className="flex flex-col w-full h-full bg-black/40 backdrop-blur-sm rounded-[5px] border border-white/10 overflow-hidden">
                <div className="flex flex-col justify-center items-center h-full p-4 text-center">
                  
                  <div className="flex flex-col items-center gap-6">
                    {/* Loading Spinner */}
                    <div className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
} 